import {
  Dispatch,
  SetStateAction,
  SyntheticEvent,
  useEffect,
  useState
} from "react";

import { useTranslation } from "react-i18next";

import Autocomplete from "@/components/Autocomplete/Autocomplete";
import BannerMessage, {
  BANNER_MESSAGE_STATUSES
} from "@/components/BannerMessage/BannerMessage";
import InputField from "@/components/InputField/InputField";
import { useAuth } from "@/context/Auth/AuthProvider";
import { CompaniesDto, TABLE_NAMES, inputFieldMarginBottom } from "@/shared";
import SelectedRowsTag from "@/shared/components/tags/SelectedRowsTag";
import { useCompanyListStore } from "@/stores/useCompanyListStore";

import { getPayloadObject } from "../../../functions";

interface IMoveVehiclesDialogContentProps {
  selectedRows: string[];
  setPayload: Dispatch<SetStateAction<object | null>>;
  resourceName: TABLE_NAMES | undefined;
}

const MoveVehiclesDialogContent = ({
  selectedRows,
  setPayload,
  resourceName
}: IMoveVehiclesDialogContentProps) => {
  const { t } = useTranslation();

  const { user } = useAuth();

  const { companyList } = useCompanyListStore();

  const [showBanner, setShowBanner] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState<CompaniesDto | null>(
    null
  );

  const resourceCount = selectedRows?.length;

  const payloadObject = getPayloadObject(resourceName, selectedRows);

  useEffect(() => {
    setPayload(payloadObject);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows, setPayload]);

  const handleCloseBanner = () => setShowBanner(false);

  const companyListWithoutSource = companyList.filter(
    (company) => company.id !== user?.selectedCompany?.id
  );

  const handleChangeTargetCompany = (value: CompaniesDto) => {
    setSelectedCompany(value);

    setPayload((prevPayload) => ({
      ...prevPayload,
      company: value
    }));
  };

  return (
    <>
      <SelectedRowsTag
        resourceName={resourceName}
        resourceCount={resourceCount}
      />

      <InputField
        id="move-vehicles-from-company-field"
        size="medium"
        data-testid="parent-company-name"
        fullWidth
        readOnly
        labelLeft={t("Dialog##from")}
        value={user?.selectedCompany.name ?? ""}
        customStyle={{ margin: "16px 0" }}
      />

      <Autocomplete
        size="medium"
        labelLeft={t("Dialog##to")}
        fullWidth
        disableClearable
        value={selectedCompany}
        options={companyListWithoutSource}
        getOptionLabel={(option) => option?.name}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        customStyle={inputFieldMarginBottom}
        onChange={(_: SyntheticEvent, value: CompaniesDto) =>
          handleChangeTargetCompany(value)
        }
        textFieldParams={{
          placeholder: t("Dialog##select a company")
        }}
      />

      {showBanner && (
        <BannerMessage
          data-testid="move-vehicles-info-banner"
          status={BANNER_MESSAGE_STATUSES.Info}
          title={t("Dialog##move vehicles info")}
          externalCustomStyle={inputFieldMarginBottom}
          close={handleCloseBanner}
        />
      )}
    </>
  );
};

export default MoveVehiclesDialogContent;
